import type {
  TankPerspectiveLabels,
  TankReadingsOverviewRow,
} from '@/api/hooks/useTankReadings'
import type { components } from '@/types/api/v1'
import { formatVolume } from '@/utils/stringFormatters'

type VolumeUnits = components['schemas']['VolumeUnits']

/**
 * Builds the "Tank – Product" label for a perspective. Appends the product
 * name in parentheses only when present; the master/Gravitate perspective has
 * no product, so it renders just the tank label there.
 */
export function tankProductLabel(labels: TankPerspectiveLabels): string {
  const tank = labels.tank_label ?? '-'
  return labels.product_name ? `${tank} (${labels.product_name})` : tank
}

/**
 * Formats a reading's volume using the shared {@link formatVolume} utility so
 * units are mapped consistently (e.g. "gallons" → "gal", "liters" → "L");
 * returns '-' when there is no reading or no volume.
 */
export function formatReadingVolume(
  reading: TankReadingsOverviewRow['reading'],
): string {
  if (reading?.volume == null) return '-'
  // The reading's unit is typed loosely as `string` by the backend schema;
  // narrow it to the shared formatter's VolumeUnits (it maps "liters" → "L"
  // and treats anything else, including unknown strings, as "gal").
  return formatVolume(reading.volume, reading.unit as VolumeUnits | null)
}
